/**
 * Регистрация, вход и перенос гостевого прогресса.
 *
 * Порядок шагов важен: сначала аккаунт, затем подтверждение адреса (оно же
 * выдаёт trial), и только потом перенос установки. Перенос до подтверждения
 * позволил бы занять чужую установку неподтверждённым аккаунтом
 * (ТЗ FR-WS-07, FR-SUB-11). У входа порядок тот же: сессия, перенос, trial.
 */

import type { InstallationId, SyncId } from '@type2x/shared-contracts';
import { claimForUser, type Workspace } from '@type2x/feature-personal-workspace';

import type {
  AccountsGateway,
  ProgressOutcome,
  ProgressTransfer,
  RegistrationForm,
  RegistrationOutcome,
  SignedIn,
  VerifiedAccount,
} from '../contracts/registration.js';
import { RegistrationError } from '../contracts/registration.js';

/** Минимальная длина пароля. Совпадает с серверной проверкой. */
export const MIN_PASSWORD_LENGTH = 10;

const EMAIL_PATTERN = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/;

/** Проверяет адрес до отправки: пустой запрос к серверу не нужен. */
export function validateEmail(email: string): void {
  if (!EMAIL_PATTERN.test(email.trim())) {
    throw new RegistrationError('invalid-email');
  }
}

/** Проверяет форму до отправки: пустой запрос к серверу не нужен. */
export function validateForm(form: RegistrationForm): void {
  validateEmail(form.email);
  if (form.password.length < MIN_PASSWORD_LENGTH) {
    throw new RegistrationError('weak-password');
  }
}

/**
 * Регистрирует пользователя.
 *
 * Возвращает подтверждённый аккаунт, только когда сервер отдал токен в
 * ответе — так он делает, пока не доставляет писем. С настоящей доставкой
 * обычным исходом становится ожидание письма, и это не ошибка.
 *
 * @throws {RegistrationError} с причиной, пригодной для показа пользователю.
 */
export async function registerAndClaim(
  gateway: AccountsGateway,
  form: RegistrationForm,
  installationId: InstallationId,
  syncId: SyncId,
): Promise<RegistrationOutcome> {
  validateForm(form);

  const registered = await gateway.register(form, installationId);
  if (registered.verificationToken === null) {
    // Письмо ушло. Секрет остаётся у этого браузера: подтвердить адрес могут
    // и на другом устройстве, а сессию он должен получить и здесь.
    return { kind: 'awaiting-verification', claimToken: registered.claimToken };
  }

  return verifyEmailAndClaim(gateway, registered.verificationToken, { installationId, syncId });
}

/**
 * Подтверждает адрес по токену из письма и переносит гостевой прогресс.
 *
 * Порядок тот же, что и при регистрации: перенос идёт после подтверждения,
 * иначе неподтверждённый аккаунт мог бы занять чужую установку (ТЗ FR-WS-07).
 * Ссылку могли открыть и там, где прогресса нет, — тогда `transfer` пуст, и
 * переноса не происходит: пустая установка аккаунту ни к чему.
 *
 * @throws {RegistrationError} если ссылка недействительна или устарела.
 */
export async function verifyEmailAndClaim(
  gateway: AccountsGateway,
  token: string,
  transfer: ProgressTransfer | null,
): Promise<VerifiedAccount> {
  const session = await gateway.verifyEmail(token);
  const progress = await transferProgress(gateway, transfer);
  const trial = await gateway.trialStatus();

  return {
    kind: 'verified',
    userId: session.userId,
    progress,
    trialDaysLeft: trial.daysLeft,
    trialNotice: trial.notice,
  };
}

/**
 * Входит в существующий аккаунт и переносит гостевой прогресс.
 *
 * Перенос тот же, что и после подтверждения адреса, и с той же оговоркой:
 * повторный вход в том же браузере переносом уже не является, а прогресс,
 * привязанный к чужому аккаунту, остаётся на месте.
 *
 * @throws {RegistrationError} с причиной, пригодной для показа пользователю.
 */
export async function loginAndClaim(
  gateway: AccountsGateway,
  form: RegistrationForm,
  transfer: ProgressTransfer | null,
): Promise<SignedIn> {
  // Пароль не проверяется на длину: правило могло измениться после того, как
  // аккаунт был создан, и старый пароль всё ещё верен.
  validateEmail(form.email);

  const session = await gateway.login(form);
  const progress = await transferProgress(gateway, transfer);
  const trial = await gateway.trialStatus();

  return {
    kind: 'signed-in',
    userId: session.userId,
    email: session.email,
    emailVerified: session.emailVerified,
    progress,
    trialDaysLeft: trial.daysLeft,
    trialNotice: trial.notice,
  };
}

/**
 * Переносит локальный прогресс в аккаунт.
 *
 * Отказ переноса не отменяет уже открытую сессию: человек вошёл, и сообщить
 * ему об этом важнее, чем оборвать вход из-за прогресса, который к его
 * аккаунту и не относился.
 */
async function transferProgress(
  gateway: AccountsGateway,
  transfer: ProgressTransfer | null,
): Promise<ProgressOutcome> {
  if (transfer === null) return 'none';

  try {
    const claim = await gateway.claim(transfer.installationId, transfer.syncId);
    return claim.alreadyClaimed ? 'already' : 'moved';
  } catch (error) {
    if (error instanceof RegistrationError && error.problem === 'progress-taken') return 'taken';
    throw error;
  }
}

/**
 * Забирает выгрузку всего, что сервер хранит о пользователе (ТЗ US-08).
 *
 * @throws {RegistrationError} если сервер отказал.
 */
export async function exportProgress(gateway: AccountsGateway): Promise<unknown> {
  return gateway.exportAccount();
}

/**
 * Удаляет аккаунт.
 *
 * Пароль не проверяется на длину: он лишь подтверждает намерение, а верен он
 * или нет, решает сервер.
 *
 * @throws {RegistrationError} если пароль неверен или сервер отказал.
 */
export async function deleteAccount(gateway: AccountsGateway, password: string): Promise<void> {
  await gateway.deleteAccount(password);
}

/**
 * Завершает сессию на сервере.
 *
 * Отказ сервера выходу не мешает: cookie может быть уже недействительной, а
 * человек всё равно хочет уйти. Локальное пространство забывается в любом
 * случае — иначе следующий за этим браузером увидит чужой прогресс.
 */
export async function logout(gateway: AccountsGateway): Promise<void> {
  try {
    await gateway.logout();
  } catch {
    // Сервер не ответил: сессия истечёт сама, а уйти нужно сейчас.
  }
}

/**
 * Просит сервер прислать ссылку восстановления пароля.
 *
 * Ответ одинаков для известного и неизвестного адреса, поэтому и клиенту
 * сообщать нечего сверх «письмо отправлено, если такой адрес есть».
 *
 * @throws {RegistrationError} если адрес не похож на адрес или сервер отказал.
 */
export async function requestPasswordReset(gateway: AccountsGateway, email: string): Promise<void> {
  validateEmail(email);
  await gateway.requestPasswordReset(email);
}

/**
 * Меняет пароль по токену из письма.
 *
 * Длина нового пароля проверяется, как при регистрации: это новый пароль, и
 * правило к нему применимо целиком.
 *
 * @throws {RegistrationError} если пароль короток, а ссылка недействительна.
 */
export async function confirmPasswordReset(
  gateway: AccountsGateway,
  token: string,
  password: string,
): Promise<void> {
  if (password.length < MIN_PASSWORD_LENGTH) {
    throw new RegistrationError('weak-password');
  }
  await gateway.confirmPasswordReset(token, password);
}

/**
 * Просит сервер отправить письмо подтверждения ещё раз.
 *
 * @throws {RegistrationError} если адрес не похож на адрес или сервер отказал.
 */
export async function resendVerification(gateway: AccountsGateway, email: string): Promise<void> {
  validateEmail(email);
  await gateway.resendVerification(email);
}

/**
 * Привязывает локальное пространство к аккаунту после успешной регистрации.
 *
 * Локальный прогресс не пересоздаётся: он остаётся тем же, меняется только
 * владелец (ТЗ FR-WS-07).
 */
export function attachWorkspaceToUser(
  workspace: Workspace,
  outcome: VerifiedAccount | SignedIn,
  now: string,
): Workspace {
  return claimForUser(workspace, outcome.userId, now);
}
